// ACTA_AGENT_GLOBAL_ACTION_CATALOG_V2
//
// Catalogue des capacités métier utilisateur.
// Il ne crée aucune nouvelle règle métier.
// Chaque entrée doit pointer vers une API ou
// un workflow existant d'Acta.

import {
  AGENT_ACTION_STATUS,
  AGENT_RISK,
} from "@/agent/actionContract";

const IMPLEMENTED_IDS =
  new Set([
    "navigation.documents",
    "navigation.commercial.dashboard",
    "navigation.opportunities",
    "navigation.quotes",
    "navigation.agenda",
    "navigation.contacts",
    "navigation.stats",
    "navigation.commercial.map",
    "navigation.gmao.dashboard",
    "navigation.gmao.map",
    "navigation.gmao.sav",
    "navigation.gmao.interventions",
    "navigation.gmao.planning",
    "navigation.gmao.maintenance",
    "navigation.gmao.machines",
    "navigation.gmao.parts",
    "navigation.users",
    "navigation.settings",
    "navigation.billing",
    "contact.create",
    "contact.whatsapp",
    "document.share",
      "contact.search",
    "contact.open",
    "contact.note.add",
    "contact.followup.create",
    "meeting.create",
    "meeting.update",
    "meeting.outcome",
]);

function capability(
  id,
  module,
  label,
  api,
  risk =
    AGENT_RISK.CONFIRM,
  page = ""
) {
  return Object.freeze({
    id,
    module,
    page,
    label,
    api,
    risk,
    status:
      IMPLEMENTED_IDS.has(id)
        ? AGENT_ACTION_STATUS
            .IMPLEMENTED
        : AGENT_ACTION_STATUS
            .CATALOGUED,
  });
}

export const ACTA_AGENT_CATALOG_SCOPE =
  Object.freeze({
    auditCommit:
      "5af71094e46d74634185519c44855c90e30d87f6",
    auditedBackendRoutes: 253,
    auditedFrontendApiHelpers: 295,
    included:
      "Actions métier authentifiées accessibles depuis l’application tenant.",
    excludedFamilies: [
      "auth publique",
      "routes publiques de partage",
      "SAV public QR",
      "health",
      "webhooks",
      "administration SaaS saas_admin",
      "endpoints purement techniques",
    ],
  });

export const ACTA_AGENT_CATALOG = [
  // Navigation
  ["navigation.documents","documents","Ouvrir les documents","route:/app/documents",AGENT_RISK.IMMEDIATE,"documents"],
  ["navigation.commercial.dashboard","commercial","Ouvrir l’accueil commercial","route:/app/commerce/dashboard",AGENT_RISK.IMMEDIATE,"dashboard"],
  ["navigation.opportunities","commercial","Ouvrir les affaires","route:/app/commerce/pipeline",AGENT_RISK.IMMEDIATE,"pipeline"],
  ["navigation.quotes","commercial","Ouvrir les devis","route:/app/commerce/quotes",AGENT_RISK.IMMEDIATE,"quotes"],
  ["navigation.agenda","commercial","Ouvrir l’agenda","route:/app/commerce/agenda",AGENT_RISK.IMMEDIATE,"agenda"],
  ["navigation.contacts","commercial","Ouvrir les contacts","route:/app/commerce/prospects",AGENT_RISK.IMMEDIATE,"portfolio"],
  ["navigation.stats","commercial","Ouvrir les statistiques","route:/app/commerce/stats",AGENT_RISK.IMMEDIATE,"stats"],
  ["navigation.commercial.map","commercial","Ouvrir la carte commerciale","route:/app/commerce/map",AGENT_RISK.IMMEDIATE,"map"],
  ["navigation.gmao.dashboard","gmao","Ouvrir la GMAO","route:/app/gmao/dashboard",AGENT_RISK.IMMEDIATE,"dashboard"],
  ["navigation.gmao.map","gmao","Ouvrir la carte GMAO","route:/app/gmao/map",AGENT_RISK.IMMEDIATE,"machines"],
  ["navigation.gmao.sav","gmao","Ouvrir le SAV","route:/app/gmao/sav",AGENT_RISK.IMMEDIATE,"sav"],
  ["navigation.gmao.interventions","gmao","Ouvrir les interventions","route:/app/gmao/interventions",AGENT_RISK.IMMEDIATE,"interventions"],
  ["navigation.gmao.planning","gmao","Ouvrir le planning","route:/app/gmao/planning",AGENT_RISK.IMMEDIATE,"planning"],
  ["navigation.gmao.maintenance","gmao","Ouvrir la maintenance","route:/app/gmao/maintenance",AGENT_RISK.IMMEDIATE,"maintenance"],
  ["navigation.gmao.machines","gmao","Ouvrir les machines","route:/app/gmao/machines",AGENT_RISK.IMMEDIATE,"machines"],
  ["navigation.gmao.parts","gmao","Ouvrir les pièces","route:/app/gmao/parts",AGENT_RISK.IMMEDIATE,"parts"],
  ["navigation.users","settings","Ouvrir les utilisateurs","route:/app/users",AGENT_RISK.IMMEDIATE],
  ["navigation.settings","settings","Ouvrir les paramètres","route:/app/settings",AGENT_RISK.IMMEDIATE],
  ["navigation.billing","settings","Ouvrir l’abonnement","route:/app/billing",AGENT_RISK.IMMEDIATE],

  // Contacts / CRM
  ["contact.search","commercial","Rechercher un contact","prospects.list",AGENT_RISK.IMMEDIATE,"portfolio"],
  ["contact.open","commercial","Ouvrir une fiche contact","prospects.list",AGENT_RISK.IMMEDIATE,"portfolio"],
  ["contact.create","commercial","Créer un contact","prospects.create",AGENT_RISK.CONFIRM,"portfolio"],
  ["contact.update","commercial","Modifier un contact","prospects.update",AGENT_RISK.CONFIRM,"portfolio"],
  ["contact.delete","commercial","Supprimer un contact","prospects.remove",AGENT_RISK.CRITICAL,"portfolio"],
  ["contact.bulkDelete","commercial","Supprimer plusieurs contacts","prospects.bulkDelete",AGENT_RISK.CRITICAL,"portfolio"],
  ["contact.call","commercial","Appeler un contact","prospects.startCall",AGENT_RISK.WORKFLOW,"portfolio"],
  ["contact.whatsapp","commercial","Appeler via WhatsApp","prospects.startCall",AGENT_RISK.WORKFLOW,"portfolio"],
  ["contact.callOutcome","commercial","Enregistrer le résultat d’un appel","prospects.recordCallOutcome",AGENT_RISK.CONFIRM,"portfolio"],
  ["contact.note.add","commercial","Ajouter une note","prospects.addNote",AGENT_RISK.CONFIRM,"portfolio"],
  ["contact.followup.create","commercial","Créer une relance","prospects.addNote",AGENT_RISK.WORKFLOW,"portfolio"],
  ["contact.note.update","commercial","Modifier une note","prospects.updateNote",AGENT_RISK.CONFIRM,"portfolio"],
  ["contact.note.delete","commercial","Supprimer une note","prospects.removeNote",AGENT_RISK.CRITICAL,"portfolio"],
  ["contact.activity.update","commercial","Mettre à jour une activité","prospects.updateActivityState",AGENT_RISK.CONFIRM,"portfolio"],
  ["contact.status.update","commercial","Modifier le statut commercial","prospects.setStatus",AGENT_RISK.CONFIRM,"portfolio"],
  ["contact.commercial.update","commercial","Modifier les informations commerciales","prospects.updateCommercial",AGENT_RISK.CONFIRM,"portfolio"],
  ["contact.assign","commercial","Affecter un commercial","prospects.assignCommercial",AGENT_RISK.CONFIRM,"portfolio"],
  ["contact.convertClient","commercial","Convertir en client","prospects.convertToClient",AGENT_RISK.CONFIRM,"portfolio"],
  ["contact.import.preview","commercial","Prévisualiser un import contacts","prospects.analyzeImport",AGENT_RISK.IMMEDIATE,"portfolio"],
  ["contact.import.execute","commercial","Importer des contacts","prospects.importCsv",AGENT_RISK.CONFIRM,"portfolio"],
  ["contact.share.reply","commercial","Répondre à un échange client","prospects.replyToShare",AGENT_RISK.CONFIRM,"portfolio"],

  // Affaires
  ["opportunity.search","commercial","Rechercher une affaire","prospectOpportunities.pipelineBoard",AGENT_RISK.IMMEDIATE,"pipeline"],
  ["opportunity.open","commercial","Ouvrir une affaire","prospectOpportunities.get",AGENT_RISK.IMMEDIATE,"pipeline"],
  ["opportunity.create","commercial","Créer une affaire","prospectOpportunities.create",AGENT_RISK.CONFIRM,"pipeline"],
  ["opportunity.update","commercial","Modifier une affaire","prospectOpportunities.update",AGENT_RISK.CONFIRM,"pipeline"],
  ["opportunity.duplicate","commercial","Dupliquer une affaire","prospectOpportunities.duplicate",AGENT_RISK.CONFIRM,"pipeline"],
  ["opportunity.delete","commercial","Supprimer une affaire","prospectOpportunities.remove",AGENT_RISK.CRITICAL,"pipeline"],
  ["opportunity.moveStage","commercial","Changer l’étape d’une affaire","prospectOpportunities.moveStage",AGENT_RISK.CONFIRM,"pipeline"],
  ["opportunity.pipelineTransition","commercial","Faire avancer une affaire","prospects.pipelineTransition",AGENT_RISK.CONFIRM,"pipeline"],
  ["opportunity.contactLost","commercial","Marquer le contact perdu","prospectOpportunities.markContactLost",AGENT_RISK.CRITICAL,"pipeline"],
  ["opportunity.reactivate","commercial","Réactiver une affaire","prospectOpportunities.reactivateContact",AGENT_RISK.CONFIRM,"pipeline"],
  ["opportunity.finalization.submit","commercial","Soumettre la finalisation","prospectOpportunities.submitFinalization",AGENT_RISK.CRITICAL,"pipeline"],
  ["opportunity.paymentProof.upload","commercial","Ajouter un justificatif de paiement","prospectOpportunities.uploadPaymentProof",AGENT_RISK.CONFIRM,"pipeline"],
  ["opportunity.payment.validate","commercial","Valider le paiement","prospectOpportunities.validateOpportunityPayment",AGENT_RISK.CRITICAL,"pipeline"],
  ["opportunity.quote.create","commercial","Créer le devis d’une affaire","quotes.createFromOpportunity",AGENT_RISK.CONFIRM,"pipeline"],

  // Devis / commandes
  ["quote.search","commercial","Rechercher un devis","quotes.list",AGENT_RISK.IMMEDIATE,"quotes"],
  ["quote.open","commercial","Ouvrir un devis","quotes.list",AGENT_RISK.IMMEDIATE,"quotes"],
  ["quote.create","commercial","Créer un devis","quotes.create",AGENT_RISK.CONFIRM,"quotes"],
  ["quote.update","commercial","Modifier un devis","quotes.update",AGENT_RISK.CONFIRM,"quotes"],
  ["quote.submit","commercial","Soumettre un devis","quotes.submit",AGENT_RISK.CONFIRM,"quotes"],
  ["quote.approve","commercial","Valider un devis","quotes.approve",AGENT_RISK.CRITICAL,"quotes"],
  ["quote.reject","commercial","Rejeter un devis","quotes.reject",AGENT_RISK.CRITICAL,"quotes"],
  ["quote.send","commercial","Envoyer un devis","quotes.send",AGENT_RISK.CONFIRM,"quotes"],
  ["quote.share","commercial","Partager un devis","quotes.share",AGENT_RISK.CONFIRM,"quotes"],
  ["quote.accept","commercial","Accepter un devis","quotes.accept",AGENT_RISK.CRITICAL,"quotes"],
  ["quote.refuse","commercial","Refuser un devis","quotes.refuse",AGENT_RISK.CRITICAL,"quotes"],
  ["quote.financing.decide","commercial","Décider du financement","quotes.decideFinancing",AGENT_RISK.CRITICAL,"quotes"],
  ["quote.sale.finalize","commercial","Finaliser une vente","quotes.finalizeSale",AGENT_RISK.CRITICAL,"quotes"],
  ["quote.purchaseOrder.create","commercial","Créer un bon de commande","quotes.createPurchaseOrder",AGENT_RISK.CRITICAL,"quotes"],
  ["quote.pdf.download","commercial","Télécharger un devis","quotes.downloadPdf",AGENT_RISK.IMMEDIATE,"quotes"],
  ["purchaseOrder.list","commercial","Lister les bons de commande","quotes.listPurchaseOrders",AGENT_RISK.IMMEDIATE,"quotes"],
  ["purchaseOrder.pdf.download","commercial","Télécharger un bon de commande","quotes.downloadPurchaseOrderPdf",AGENT_RISK.IMMEDIATE,"quotes"],
  ["purchaseOrder.sign","commercial","Valider la signature du bon de commande","quotes.signPurchaseOrder",AGENT_RISK.CRITICAL,"quotes"],
  ["purchaseOrder.pay","commercial","Valider le paiement du bon de commande","quotes.payPurchaseOrder",AGENT_RISK.CRITICAL,"quotes"],
  ["purchaseOrder.prepareInstallation","commercial","Préparer une installation GMAO","quotes.preparePurchaseOrderInstallation",AGENT_RISK.CRITICAL,"quotes"],

  // Documents / dossiers / partages
  ["document.search","documents","Rechercher un document","documents.list",AGENT_RISK.IMMEDIATE,"documents"],
  ["document.open","documents","Ouvrir un document","documents.get",AGENT_RISK.IMMEDIATE,"documents"],
  ["document.preview","documents","Prévisualiser un document","documents.getPreview",AGENT_RISK.IMMEDIATE,"documents"],
  ["document.download","documents","Télécharger un document","documents.get",AGENT_RISK.IMMEDIATE,"documents"],
  ["document.upload","documents","Ajouter un document","documents.upload",AGENT_RISK.CONFIRM,"documents"],
  ["document.update","documents","Modifier un document","documents.update",AGENT_RISK.CONFIRM,"documents"],
  ["document.delete","documents","Supprimer un document","documents.remove",AGENT_RISK.CRITICAL,"documents"],
  ["document.bulkDelete","documents","Supprimer plusieurs documents","documents.removeMany",AGENT_RISK.CRITICAL,"documents"],
  ["document.access","documents","Gérer les accès d’un document","documents.setAccess",AGENT_RISK.CONFIRM,"documents"],
  ["document.share","documents","Partager un document","ShareModal + shares.create",AGENT_RISK.WORKFLOW,"documents"],
  ["folder.list","documents","Lister les dossiers","folders.list",AGENT_RISK.IMMEDIATE,"documents"],
  ["folder.open","documents","Ouvrir un dossier","folders.list",AGENT_RISK.IMMEDIATE,"documents"],
  ["folder.create","documents","Créer un dossier","folders.create",AGENT_RISK.CONFIRM,"documents"],
  ["folder.update","documents","Modifier un dossier","folders.update",AGENT_RISK.CONFIRM,"documents"],
  ["folder.delete","documents","Supprimer un dossier","folders.remove",AGENT_RISK.CRITICAL,"documents"],
  ["folder.access","documents","Gérer les accès d’un dossier","folders.setAccess",AGENT_RISK.CONFIRM,"documents"],
  ["share.list","documents","Lister les partages","shares.list",AGENT_RISK.IMMEDIATE,"documents"],
  ["share.open","documents","Ouvrir un partage","shares.get",AGENT_RISK.IMMEDIATE,"documents"],
  ["share.create","documents","Créer un partage","shares.create",AGENT_RISK.CONFIRM,"documents"],
  ["share.revoke","documents","Révoquer un partage","shares.revoke",AGENT_RISK.CRITICAL,"documents"],
  ["share.delete","documents","Supprimer un partage","shares.remove",AGENT_RISK.CRITICAL,"documents"],
  ["share.expiration.update","documents","Modifier l’expiration","shares.updateExpiration",AGENT_RISK.CONFIRM,"documents"],
  ["share.opens.update","documents","Modifier la limite d’ouvertures","shares.updateOpens",AGENT_RISK.CONFIRM,"documents"],

  // Agenda / échanges
  ["meeting.list","commercial","Lister les rendez-vous","prospects.agenda",AGENT_RISK.IMMEDIATE,"agenda"],
  ["meeting.create","commercial","Créer un rendez-vous","prospects.addNote",AGENT_RISK.CONFIRM,"agenda"],
  ["meeting.update","commercial","Modifier un rendez-vous","prospects.updateNote|shares.updateConfirmedExchangeMeeting",AGENT_RISK.CONFIRM,"agenda"],
  ["meeting.delete","commercial","Supprimer un rendez-vous","shares.deleteExchangeMeeting",AGENT_RISK.CRITICAL,"agenda"],
  ["meeting.outcome","commercial","Enregistrer le résultat d’un rendez-vous","prospects.recordMeetingOutcome",AGENT_RISK.CONFIRM,"agenda"],
  ["meeting.shareAppointment.create","commercial","Créer un rendez-vous depuis un partage","prospects.createShareAppointment",AGENT_RISK.CONFIRM,"agenda"],
  ["meeting.shareAppointment.update","commercial","Modifier un rendez-vous de partage","prospects.updateShareAppointment",AGENT_RISK.CONFIRM,"agenda"],
  ["exchange.message.send","commercial","Envoyer un message d’échange","shares.sendExchangeMessage",AGENT_RISK.CONFIRM,"agenda"],

  // GMAO machines
  ["machine.search","gmao","Rechercher une machine","gmao.listMachines",AGENT_RISK.IMMEDIATE,"machines"],
  ["machine.open","gmao","Ouvrir une machine","gmao.getMachine",AGENT_RISK.IMMEDIATE,"machines"],
  ["machine.map","gmao","Afficher une machine sur la carte","gmao.getMachineFleetMap",AGENT_RISK.IMMEDIATE,"machines"],
  ["machine.create","gmao","Créer une machine","gmao.createMachine",AGENT_RISK.CONFIRM,"machines"],
  ["machine.update","gmao","Modifier une machine","gmao.updateMachine",AGENT_RISK.CONFIRM,"machines"],
  ["machine.assign","gmao","Affecter une machine à un client","gmao.assignMachineToClient",AGENT_RISK.CONFIRM,"machines"],
  ["machine.status.update","gmao","Modifier le statut d’une machine","gmao.updateMachineStatus",AGENT_RISK.CONFIRM,"machines"],
  ["machine.handpiece.link","gmao","Rattacher une pièce à main","gmao.linkHandpieceParentMachine",AGENT_RISK.CONFIRM,"machines"],
  ["machine.handpiece.replace","gmao","Remplacer une pièce à main","gmao.replaceHandpiece",AGENT_RISK.CRITICAL,"machines"],
  ["machine.archive","gmao","Archiver une machine","gmao.archiveMachine",AGENT_RISK.CRITICAL,"machines"],
  ["machine.restore","gmao","Restaurer une machine","gmao.restoreMachine",AGENT_RISK.CONFIRM,"machines"],
  ["machine.meter.update","gmao","Mettre à jour le compteur machine","gmao.updateMachineMeter",AGENT_RISK.CONFIRM,"machines"],
  ["machine.history","gmao","Voir l’historique machine","gmao.getMachineHistory",AGENT_RISK.IMMEDIATE,"machines"],

  // GMAO interventions
  ["intervention.search","gmao","Rechercher une intervention","gmao.listInterventions",AGENT_RISK.IMMEDIATE,"interventions"],
  ["intervention.open","gmao","Ouvrir une intervention","gmao.getIntervention",AGENT_RISK.IMMEDIATE,"interventions"],
  ["intervention.create","gmao","Créer une intervention","gmao.createIntervention",AGENT_RISK.CONFIRM,"interventions"],
  ["intervention.update","gmao","Modifier une intervention","gmao.updateIntervention",AGENT_RISK.CONFIRM,"interventions"],
  ["intervention.cancel","gmao","Annuler une intervention","gmao.cancelIntervention",AGENT_RISK.CRITICAL,"interventions"],
  ["intervention.assign","gmao","Affecter un technicien","gmao.assignTechnician",AGENT_RISK.CONFIRM,"interventions"],
  ["intervention.transition","gmao","Changer le statut d’une intervention","gmao.transitionIntervention",AGENT_RISK.CONFIRM,"interventions"],
  ["intervention.workReport","gmao","Mettre à jour le rapport d’intervention","gmao.updateInterventionWorkReport",AGENT_RISK.CONFIRM,"interventions"],
  ["intervention.attachment.list","gmao","Lister les pièces jointes","gmao.listInterventionAttachments",AGENT_RISK.IMMEDIATE,"interventions"],
  ["intervention.attachment.upload","gmao","Ajouter une pièce jointe","gmao.uploadInterventionAttachment",AGENT_RISK.CONFIRM,"interventions"],
  ["intervention.attachment.delete","gmao","Supprimer une pièce jointe","gmao.deleteInterventionAttachment",AGENT_RISK.CRITICAL,"interventions"],
  ["intervention.report.pdf","gmao","Télécharger le rapport PDF","gmao.getInterventionReportPdfBlob",AGENT_RISK.IMMEDIATE,"interventions"],
  ["intervention.report.archive","gmao","Archiver le rapport d’intervention","gmao.archiveInterventionReport",AGENT_RISK.CONFIRM,"interventions"],

  // Maintenance
  ["maintenance.search","gmao","Rechercher un plan de maintenance","gmao.listMaintenancePlans",AGENT_RISK.IMMEDIATE,"maintenance"],
  ["maintenance.open","gmao","Ouvrir un plan de maintenance","gmao.getMaintenancePlan",AGENT_RISK.IMMEDIATE,"maintenance"],
  ["maintenance.create","gmao","Créer un plan de maintenance","gmao.createMaintenancePlan",AGENT_RISK.CONFIRM,"maintenance"],
  ["maintenance.update","gmao","Modifier un plan de maintenance","gmao.updateMaintenancePlan",AGENT_RISK.CONFIRM,"maintenance"],
  ["maintenance.activate","gmao","Activer un plan de maintenance","gmao.activateMaintenancePlan",AGENT_RISK.CONFIRM,"maintenance"],
  ["maintenance.deactivate","gmao","Désactiver un plan de maintenance","gmao.deactivateMaintenancePlan",AGENT_RISK.CRITICAL,"maintenance"],
  ["maintenance.intervention.generate","gmao","Générer une intervention de maintenance","gmao.generateMaintenanceIntervention",AGENT_RISK.CONFIRM,"maintenance"],
  ["maintenance.quote.create","gmao","Créer un devis de maintenance","gmao.createMaintenanceQuote",AGENT_RISK.CONFIRM,"maintenance"],

  // Stock / pièces
  ["part.search","gmao","Rechercher une pièce","gmao.listParts",AGENT_RISK.IMMEDIATE,"parts"],
  ["part.open","gmao","Ouvrir une pièce","gmao.getPart",AGENT_RISK.IMMEDIATE,"parts"],
  ["part.create","gmao","Créer une référence de pièce","gmao.createPart",AGENT_RISK.CONFIRM,"parts"],
  ["part.update","gmao","Modifier une pièce","gmao.updatePart",AGENT_RISK.CONFIRM,"parts"],
  ["part.archive","gmao","Archiver une pièce","gmao.archivePart",AGENT_RISK.CRITICAL,"parts"],
  ["part.restore","gmao","Restaurer une pièce","gmao.restorePart",AGENT_RISK.CONFIRM,"parts"],
  ["part.receive","gmao","Réceptionner du stock","gmao.receivePart",AGENT_RISK.CONFIRM,"parts"],
  ["part.adjust","gmao","Ajuster le stock","gmao.adjustPart",AGENT_RISK.CRITICAL,"parts"],
  ["part.consume","gmao","Consommer une pièce sur intervention","gmao.consumeInterventionPart",AGENT_RISK.CONFIRM,"parts"],
  ["part.return","gmao","Retourner une pièce consommée","gmao.returnInterventionPartUsage",AGENT_RISK.CONFIRM,"parts"],
  ["part.movements","gmao","Voir les mouvements de stock","gmao.listPartMovements",AGENT_RISK.IMMEDIATE,"parts"],

  // SAV
  ["sav.search","gmao","Rechercher un ticket SAV","gmao.listSavTickets",AGENT_RISK.IMMEDIATE,"sav"],
  ["sav.open","gmao","Ouvrir un ticket SAV","gmao.getSavTicket",AGENT_RISK.IMMEDIATE,"sav"],
  ["sav.history","gmao","Voir l’historique SAV","gmao.getSavTicketHistory",AGENT_RISK.IMMEDIATE,"sav"],
  ["sav.take","gmao","Prendre en charge un ticket SAV","gmao.takeSavTicket",AGENT_RISK.CONFIRM,"sav"],
  ["sav.assign","gmao","Affecter un technicien SAV","gmao.assignSavTicketTechnician",AGENT_RISK.CONFIRM,"sav"],
  ["sav.status.update","gmao","Modifier le statut SAV","gmao.updateSavTicketStatus",AGENT_RISK.CONFIRM,"sav"],
  ["sav.comment.add","gmao","Ajouter un commentaire SAV","gmao.addSavTicketComment",AGENT_RISK.CONFIRM,"sav"],
  ["sav.attachment.list","gmao","Lister les pièces jointes SAV","gmao.listSavTicketAttachments",AGENT_RISK.IMMEDIATE,"sav"],
  ["sav.quote.open","gmao","Voir le devis SAV","gmao.getSavTicketQuote",AGENT_RISK.IMMEDIATE,"sav"],
  ["sav.rma.list","gmao","Lister les RMA","gmao.listSavTicketRmaCases",AGENT_RISK.IMMEDIATE,"sav"],
  ["sav.rma.create","gmao","Créer un RMA","gmao.createSavTicketRma",AGENT_RISK.CONFIRM,"sav"],
  ["sav.rma.update","gmao","Modifier un RMA","gmao.updateSavTicketRma",AGENT_RISK.CONFIRM,"sav"],

  // Utilisateurs / paramètres
  ["user.list","settings","Lister les utilisateurs","users.list",AGENT_RISK.IMMEDIATE],
  ["user.create","settings","Créer un utilisateur","users.create",AGENT_RISK.CRITICAL],
  ["user.update","settings","Modifier un utilisateur","users.update",AGENT_RISK.CRITICAL],
  ["user.delete","settings","Supprimer un utilisateur","users.remove",AGENT_RISK.CRITICAL],
  ["user.unlock","settings","Déverrouiller un utilisateur","users.unlock",AGENT_RISK.CRITICAL],
  ["profile.update","settings","Modifier mon profil","auth.updateMe",AGENT_RISK.CONFIRM],
  ["profile.contact.update","settings","Modifier mes coordonnées","auth.updateMyContact",AGENT_RISK.CONFIRM],
  ["profile.password.change","settings","Changer mon mot de passe","auth.changePassword",AGENT_RISK.CRITICAL],
  ["branding.open","settings","Voir l’identité du tenant","tenant.getBranding",AGENT_RISK.IMMEDIATE],
  ["branding.update","settings","Modifier l’identité du tenant","tenant.updateBranding",AGENT_RISK.CRITICAL],
  ["branding.logo.upload","settings","Modifier le logo","tenant.uploadLogo",AGENT_RISK.CRITICAL],
  ["scoring.open","settings","Voir le scoring","tenant.getScoring",AGENT_RISK.IMMEDIATE],
  ["scoring.preview","settings","Simuler le scoring","tenant.previewScoring",AGENT_RISK.IMMEDIATE],
  ["scoring.update","settings","Modifier le scoring","tenant.updateScoring",AGENT_RISK.CRITICAL],
  ["notification.preferences.open","settings","Voir mes préférences de notification","notificationSettings.me",AGENT_RISK.IMMEDIATE],
  ["notification.preferences.update","settings","Modifier mes notifications","notificationSettings.updateMe",AGENT_RISK.CONFIRM],
  ["notification.tenant.update","settings","Modifier les notifications du tenant","notificationSettings.updateTenant",AGENT_RISK.CRITICAL],
  ["billing.plans","settings","Voir les abonnements","billing.plans",AGENT_RISK.IMMEDIATE],
  ["billing.status","settings","Voir l’état de l’abonnement","billing.status",AGENT_RISK.IMMEDIATE],
  ["billing.transactions","settings","Voir les transactions","billing.transactions",AGENT_RISK.IMMEDIATE],
  ["billing.requestPlan","settings","Demander un changement de plan","billing.requestPlan",AGENT_RISK.CONFIRM],
  ["billing.checkout","settings","Ouvrir le paiement","billing.checkout",AGENT_RISK.CRITICAL],
].map(
  ([
    id,
    module,
    label,
    api,
    risk,
    page,
  ]) =>
    capability(
      id,
      module,
      label,
      api,
      risk,
      page
    )
);
